import React from "react";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import { X } from "lucide-react";

import { Dialog, DialogContent } from "./Dialog";

/**
 * UnifiedModal
 * A common modal structure used across the application.
 *
 * Props:
 * - open (boolean): Whether the modal is open.
 * - onClose (function): Callback triggered when the modal should close.
 * - title (string | ReactNode): Header title of the modal.
 * - children (ReactNode): Modal body content.
 * - maxWidth (string): MUI maxWidth for the underlying Dialog (default "sm").
 * - contentSx (object): Custom sx for the body content wrapper.
 */
export const UnifiedModal = ({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = "sm",
  contentSx = {},
  disableExtremeZIndex = false,
  hideCloseButton = false,
  ...dialogProps
}) => {
  const dialogSx = disableExtremeZIndex
    ? dialogProps?.sx
    : {
      ...(dialogProps?.sx || {}),
      zIndex: 100000,
    };

  return (
    <Dialog
      open={open}
      onOpenChange={onClose}
      maxWidth={maxWidth}
      fullWidth
      {...dialogProps}
      sx={dialogSx}
    >
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh", // Limita la altura máxima del modal
          overflow: "hidden", // Evita scroll en el contenedor principal
        }}
      >
        {/* Header - Fijo en la parte superior */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 24px 12px 24px",
            flexShrink: 0, // Evita que se comprima
            position: "sticky",
            top: 0,
            backgroundColor: "background.paper",
            zIndex: 1,
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.3 }}
          >
            {title}
          </Typography>

          {!hideCloseButton && (
            <IconButton aria-label="Cerrar modal" onClick={() => onClose?.(false)}>
              <X size={20} />
            </IconButton>
          )}
        </Box>

        {/* Divider - También fijo */}
        <Divider sx={{ flexShrink: 0 }} />

        {/* Body - Con scroll cuando sea necesario */}
        <Box
          sx={{
            p: "24px",
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            ...contentSx,
            borderBottomLeftRadius: footer ? 0 : "12px",
            borderBottomRightRadius: footer ? 0 : "12px",
          }}
        >
          {children}
        </Box>

        {/* Footer fijo - no scrollable */}
        {footer && (
          <>
            <Divider sx={{ flexShrink: 0 }} />
            <Box
              sx={{
                p: "16px 24px",
                flexShrink: 0,
                borderBottomLeftRadius: "12px",
                borderBottomRightRadius: "12px",
                backgroundColor: "background.paper",
              }}
            >
              {footer}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
